import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  HostListener,
  ElementRef,
  ViewChild
} from '@angular/core';
import { Photo } from '../../entities/photo/photo.entity';
import { NgClass } from '@angular/common';
import {TranslocoPipe} from '@jsverse/transloco';
import {PortfolioPhoto} from '../../entities/portfolio/portfolio-photo.entity';
import {AppImageComponent} from '../image/image';

@Component({
  selector: 'app-carousel',
  templateUrl: './carousel.component.html',
  imports: [
    NgClass,
    TranslocoPipe,
    AppImageComponent
  ],
  styleUrls: ['./carousel.component.scss']
})
export class CarouselComponent implements OnChanges {
  @Input() open = false;
  @Input() photos: Photo[] | PortfolioPhoto[] | null = null;
  @Input() index = 0;
  @Output() onClose = new EventEmitter<void>();
  @ViewChild('frame') frameRef?: ElementRef<HTMLElement>;

  public currentImage: Photo | null = null;
  public dragging = false;
  private dragStartX: number|null = null;
  private dragOffset = 0;
  private capturedPointerTarget: HTMLElement|null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['photos'] || changes['index'] || changes['open']) {
      this.setCurrentImage(this.index);
    }
  }

  setCurrentImage(index: number): void {
    if (this.photos && this.photos.length > 0 && index >= 0 && index < this.photos.length) {
      this.currentImage = this.photos[index];
    } else {
      this.currentImage = null;
    }
  }

  close(): void {
    this.onClose.emit();
  }

  prev(): void {
    if (this.photos && this.index > 0) {
      this.index--;
      this.setCurrentImage(this.index);
    }
  }

  next(): void {
    if (this.photos && this.index < this.photos.length - 1) {
      this.index++;
      this.setCurrentImage(this.index);
    }
  }

  onPointerDown(event: PointerEvent): void {
    if (!this.photos) return;
    if ((event.target as HTMLElement).closest('.carousel__control')) return;
    this.dragging = true;
    this.dragStartX = event.clientX;
    this.dragOffset = 0;
    const target = event.target as HTMLElement | null;
    target?.setPointerCapture?.(event.pointerId);
    this.capturedPointerTarget = target;
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.dragging || this.dragStartX === null) return;
    const rawOffset = event.clientX - this.dragStartX;
    this.dragOffset = this.clampDelta(rawOffset);
  }

  onPointerUp(event: PointerEvent): void {
    if (!this.dragging || this.dragStartX === null) {
      this.resetDrag(event.pointerId);
      return;
    }

    const delta = this.clampDelta(event.clientX - this.dragStartX);
    const frameWidth = this.frameRef?.nativeElement.clientWidth ?? 0;
    const length = this.photos?.length ?? 0;
    if (frameWidth > 0 && length > 0) {
      const targetIndex = this.index - delta / frameWidth;
      const snappedIndex = Math.round(targetIndex);
      const clampedIndex = Math.max(0, Math.min(length - 1, snappedIndex));
      if (clampedIndex !== this.index) {
        this.index = clampedIndex;
        this.setCurrentImage(this.index);
      }
    }

    this.resetDrag(event.pointerId);
  }

  public get trackTransform(): string {
    const offset = this.dragging ? `${this.dragOffset}px` : '0px';
    return `translateX(calc(-${this.index * 100}% + ${offset}))`;
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboard(event: KeyboardEvent): void {
    if (!this.open) return;

    if (event.key === 'ArrowLeft') {
      this.prev();
    } else if (event.key === 'ArrowRight') {
      this.next();
    } else if (event.key === 'Escape') {
      this.close();
    }
  }

  private resetDrag(pointerId?: number): void {
    if (pointerId !== undefined && this.capturedPointerTarget) {
      this.capturedPointerTarget.releasePointerCapture?.(pointerId);
    }
    this.dragging = false;
    this.dragStartX = null;
    this.dragOffset = 0;
    this.capturedPointerTarget = null;
  }

  private clampDelta(delta: number): number {
    const length = this.photos?.length ?? 0;
    if (length === 0) return 0;
    if (this.index === 0 && delta > 0) return 0;
    if (length > 0 && this.index === length - 1 && delta < 0) return 0;
    return delta;
  }
}
